import React, { useState } from "react";
import { AlertCircle, Copy, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { authAPI, persistAuthSession, type LoginUser } from "@/lib/api";
import { applyFailedMfa } from "./loginErrors";
import { MfaStoreBadges } from "./MfaStoreBadges";
import { OtpSixBoxes } from "./OtpSixBoxes";

interface Props {
  enrollToken: string;
  onComplete: (user: LoginUser, token: string) => void;
  onBack: () => void;
}

export function MfaEnrollWizard({ enrollToken, onComplete, onBack }: Props) {
  const [step, setStep] = useState(0);
  const [qrPng, setQrPng] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [savedCodes, setSavedCodes] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingSession, setPendingSession] = useState<{
    user: LoginUser;
    token: string;
    refreshToken: string;
  } | null>(null);

  const loadQr = async () => {
    setLoading(true);
    setError("");
    try {
      const start = await authAPI.mfaEnrollStart(enrollToken);
      setQrPng(start.qrPng);
      setSecret(start.secret);
      setStep(1);
    } catch (err: unknown) {
      const failed = applyFailedMfa(err);
      setError(failed.error);
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (code.replace(/\D/g, "").length !== 6) {
      setError("Ingresá el código de 6 dígitos de la app.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await authAPI.mfaEnrollConfirm(enrollToken, code, rememberDevice);
      setPendingSession({
        user: result.user,
        token: result.token,
        refreshToken: result.refreshToken,
      });
      setBackupCodes(result.backupCodes);
      setCode("");
      setStep(3);
    } catch (err: unknown) {
      const failed = applyFailedMfa(err);
      setCode(failed.code);
      setError(failed.error);
    } finally {
      setLoading(false);
    }
  };

  const finish = () => {
    if (!pendingSession || !savedCodes) return;
    persistAuthSession(pendingSession);
    onComplete(pendingSession.user, pendingSession.token);
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-4">
      {step === 0 && (
        <>
          <div className="flex items-center gap-3 text-[#1a2744]">
            <Smartphone className="h-8 w-8 shrink-0" />
            <h2 className="text-lg font-semibold">Configurá tu authenticator</h2>
          </div>
          <p className="text-sm text-gray-600">
            Instalá una app authenticator en tu teléfono (no en el del administrador):
            Google Authenticator, Microsoft Authenticator, Authy, 1Password, Aegis o
            Contraseñas de iOS.
          </p>
          <MfaStoreBadges />
          {error && (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>No se pudo continuar</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onBack} className="flex-1">
              Volver
            </Button>
            <Button
              type="button"
              onClick={loadQr}
              disabled={loading}
              className="flex-1 bg-[#d97706] hover:bg-[#b86607] text-white"
            >
              {loading ? "Cargando..." : "Continuar"}
            </Button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <h2 className="text-lg font-semibold text-gray-900">Escaneá el código QR</h2>
          <p className="text-sm text-gray-600">
            Usá la app authenticator de <span className="font-medium">tu</span> teléfono.
          </p>
          {qrPng ? (
            <div className="flex justify-center bg-white p-3 rounded-lg border">
              <img src={qrPng} alt="Código QR TOTP" className="w-48 h-48" />
            </div>
          ) : null}
          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Si no podés escanear, ingresá este secreto:
            </p>
            <div className="flex gap-2">
              <code className="flex-1 text-xs break-all bg-gray-100 px-3 py-2 rounded-lg text-gray-900">
                {secret}
              </code>
              <Button type="button" variant="outline" size="sm" onClick={() => copy(secret)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onBack} className="flex-1">
              Volver
            </Button>
            <Button
              type="button"
              onClick={() => {
                setCode("");
                setError("");
                setStep(2);
              }}
              className="flex-1 bg-[#d97706] hover:bg-[#b86607] text-white"
            >
              Ya lo escaneé
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <form onSubmit={confirmCode} className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Confirmá el código de 6 dígitos</h2>
          <p className="text-sm text-gray-600">
            Ingresá el código que muestra la app. Hasta que verifique, MFA no queda activo.
          </p>
          <OtpSixBoxes
            idPrefix="enroll-otp"
            value={code}
            onChange={(val) => {
              setCode(val);
              setError("");
            }}
            disabled={loading}
          />
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              disabled={loading}
              className="mt-1"
            />
            Recordar este dispositivo 24 horas
          </label>
          {error && (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>Código incorrecto</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
              Volver al QR
            </Button>
            <Button
              type="submit"
              disabled={loading || code.replace(/\D/g, "").length !== 6}
              className="flex-1 bg-[#d97706] hover:bg-[#b86607] text-white"
            >
              {loading ? "Verificando..." : "Verificar"}
            </Button>
          </div>
        </form>
      )}

      {step === 3 && (
        <>
          <h2 className="text-lg font-semibold text-gray-900">Códigos de respaldo</h2>
          <p className="text-sm text-gray-600">
            Guardalos en un lugar seguro. Se muestran una sola vez. Cada uno se usa una
            vez si perdés el teléfono.
          </p>
          <ul className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 border rounded-lg p-3">
            {backupCodes.map((c) => (
              <li key={c} className="text-gray-900">
                {c}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => copy(backupCodes.join("\n"))}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copiar todos
          </Button>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={savedCodes}
              onChange={(e) => setSavedCodes(e.target.checked)}
              className="mt-1"
            />
            Ya guardé estos códigos en un lugar seguro.
          </label>
          <Button
            type="button"
            disabled={!savedCodes}
            onClick={finish}
            className="w-full bg-[#d97706] hover:bg-[#b86607] text-white"
          >
            Entrar a la aplicación
          </Button>
        </>
      )}
    </div>
  );
}
